/**
 * System Settings View
 * Global system configuration
 */

import React, { useState, useEffect } from 'react';
import { Icons } from '../common/Icons';
import { systemService, systemSettingsService, tenantsService, addonCatalogService } from '../../services/api';

const SystemSettingsView = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    system_name: 'Business Manager',
    default_timezone: 'America/Chicago',
    default_tax_rate: 8.25,
    maintenance_mode: false,
    allow_self_registration: false,
    require_email_verification: true,
    session_timeout_minutes: 480,
    max_file_upload_mb: 10
  });
  const [message, setMessage] = useState(null);

  // Platform billing tenant (real persisted setting, separate from the demo
  // settings object above).
  const [billingTenantId, setBillingTenantId] = useState('');     // current saved value
  const [billingTenantName, setBillingTenantName] = useState(''); // resolved label
  const [pickTenantId, setPickTenantId] = useState('');           // dropdown selection
  const [tenantOptions, setTenantOptions] = useState([]);
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingMessage, setBillingMessage] = useState(null);

  // Billable add-on catalog prices (platform-wide; Portal list price).
  const [addonCatalog, setAddonCatalog] = useState([]);
  const [addonPriceDrafts, setAddonPriceDrafts] = useState({}); // slug -> dollars string
  const [addonSavingSlug, setAddonSavingSlug] = useState(null);
  const [addonMessage, setAddonMessage] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await systemService.getSettings().catch(() => null);
      if (response?.data) {
        setSettings(response.data);
      }

      // Load the real platform-billing-tenant setting + tenant options + add-on prices.
      const [billing, tenants, catalog] = await Promise.all([
        systemSettingsService.get('platform_billing_tenant_id').catch(() => null),
        tenantsService.getAll({ status: 'active', limit: 200 }).catch(() => null),
        addonCatalogService.getAll().catch(() => null),
      ]);

      if (billing?.data) {
        const val = billing.data.value || '';
        setBillingTenantId(val);
        setPickTenantId(val);
        setBillingTenantName(billing.data.resolved_tenant?.name || '');
      }
      if (tenants?.data) {
        setTenantOptions(tenants.data);
      }
      if (catalog?.data?.addons) {
        setAddonCatalog(catalog.data.addons);
        const drafts = {};
        for (const a of catalog.data.addons) {
          drafts[a.slug] = String(a.price_monthly ?? (a.price_cents / 100));
        }
        setAddonPriceDrafts(drafts);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAddonPrice = async (slug) => {
    setAddonSavingSlug(slug);
    setAddonMessage(null);
    try {
      const dollars = parseFloat(addonPriceDrafts[slug]);
      if (!Number.isFinite(dollars) || dollars < 0) {
        setAddonMessage({ type: 'error', text: 'Enter a valid monthly price (USD).' });
        return;
      }
      const priceCents = Math.round(dollars * 100);
      const res = await addonCatalogService.updatePrice(slug, priceCents);
      const updated = res.data?.addon;
      if (updated) {
        setAddonCatalog((prev) =>
          prev.map((a) => (a.slug === slug ? { ...a, ...updated } : a))
        );
        setAddonPriceDrafts((prev) => ({
          ...prev,
          [slug]: String(updated.price_monthly ?? updated.price_cents / 100),
        }));
      }
      setAddonMessage({
        type: 'success',
        text: res.message || 'Add-on price updated. Portal list price and new checkouts use this amount.',
      });
      setTimeout(() => setAddonMessage(null), 4000);
    } catch (err) {
      setAddonMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to update add-on price.',
      });
    } finally {
      setAddonSavingSlug(null);
    }
  };

  const handleSaveBillingTenant = async () => {
    setBillingSaving(true);
    setBillingMessage(null);
    try {
      const res = await systemSettingsService.update(
        'platform_billing_tenant_id',
        pickTenantId || null
      );
      const newVal = res.data?.value || '';
      setBillingTenantId(newVal);
      const match = tenantOptions.find(t => t.id === newVal);
      setBillingTenantName(match?.name || '');
      setBillingMessage({ type: 'success', text: 'Platform billing tenant updated.' });
      setTimeout(() => setBillingMessage(null), 3000);
    } catch (err) {
      setBillingMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to update platform billing tenant.',
      });
    } finally {
      setBillingSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await systemService.updateSettings(settings).catch(() => {
        console.log('Demo mode: Settings saved locally');
      });
      setMessage({ type: 'success', text: 'Settings saved successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={48} className="spin" />
        <p>Loading settings...</p>
        <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px' }}>
      {message && (
        <div className={`alert alert-${message.type} mb-3`}>
          {message.type === 'success' ? <Icons.CheckCircle size={18} /> : <Icons.AlertCircle size={18} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* General Settings */}
      <div className="card mb-3">
        <h3 className="card-title mb-3">General Settings</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">System Name</label>
            <input
              type="text"
              className="form-control"
              value={settings.system_name}
              onChange={(e) => setSettings({ ...settings, system_name: e.target.value })}
            />
            <p className="form-hint">Display name for the system</p>
          </div>
          <div className="form-group">
            <label className="form-label">Default Timezone</label>
            <select
              className="form-control"
              value={settings.default_timezone}
              onChange={(e) => setSettings({ ...settings, default_timezone: e.target.value })}
            >
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Default Tax Rate (%)</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              value={settings.default_tax_rate}
              onChange={(e) => setSettings({ ...settings, default_tax_rate: parseFloat(e.target.value) })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Max File Upload (MB)</label>
            <input
              type="number"
              className="form-control"
              value={settings.max_file_upload_mb}
              onChange={(e) => setSettings({ ...settings, max_file_upload_mb: parseInt(e.target.value) })}
            />
          </div>
        </div>
      </div>

      {/* Platform Billing */}
      <div className="card mb-3">
        <h3 className="card-title mb-3">Platform Billing</h3>

        {billingMessage && (
          <div className={`alert alert-${billingMessage.type} mb-2`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {billingMessage.type === 'success' ? <Icons.CheckCircle size={16} /> : <Icons.AlertCircle size={16} />}
            <span>{billingMessage.text}</span>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Billing Tenant (revenue recipient)</label>
          <select
            className="form-control"
            value={pickTenantId}
            onChange={(e) => setPickTenantId(e.target.value)}
            style={{ maxWidth: '420px' }}
          >
            <option value="">— Not set —</option>
            {tenantOptions.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <p className="form-hint">
            The tenant whose books receive revenue from Business Manager subscription
            invoices issued to other tenants. Set this to your operating company
            (e.g. CR Hood Solutions). This records the mapping; journal-entry posting
            from Stripe payments is configured separately.
          </p>
          {billingTenantId && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
              Currently mapped to: <strong>{billingTenantName || billingTenantId}</strong>
            </div>
          )}
          {!billingTenantId && (
            <div className="alert alert-warning mt-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Icons.AlertCircle size={16} />
              <span>No billing tenant mapped. Subscription revenue has no designated recipient.</span>
            </div>
          )}
        </div>

        <button
          className="btn btn-primary btn-sm"
          onClick={handleSaveBillingTenant}
          disabled={billingSaving || pickTenantId === billingTenantId}
        >
          {billingSaving
            ? <><Icons.Loader size={16} className="spin" /> Saving…</>
            : <><Icons.Check size={16} /> Save Billing Tenant</>}
        </button>
      </div>

      {/* Billable add-on catalog prices */}
      <div className="card mb-3">
        <h3 className="card-title mb-3">Billable Add-ons</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 0 }}>
          Platform list prices for optional modules (e.g. Payroll & HR), shown on locked Portal tiles.
          Changing a price updates new Stripe checkouts; existing Stripe subscriptions keep their
          contracted amount until changed in Stripe. Base subscription bundles are managed under{' '}
          <strong>Subscription Plans</strong>. Per-tenant complimentary grants remain on each
          tenant&apos;s <strong>Add-ons</strong> tab.
        </p>

        {addonMessage && (
          <div className={`alert alert-${addonMessage.type} mb-2`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {addonMessage.type === 'success' ? <Icons.CheckCircle size={16} /> : <Icons.AlertCircle size={16} />}
            <span>{addonMessage.text}</span>
          </div>
        )}

        {addonCatalog.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No billable add-ons configured.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {addonCatalog.map((addon) => {
              const draft = addonPriceDrafts[addon.slug] ?? '';
              const savedDollars = String(addon.price_monthly ?? addon.price_cents / 100);
              const dirty = draft !== savedDollars;
              const savingAddon = addonSavingSlug === addon.slug;
              return (
                <div
                  key={addon.slug}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    alignItems: 'flex-end',
                    paddingTop: 12,
                    borderTop: '1px solid var(--border-color)',
                  }}
                >
                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{addon.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{addon.slug}</div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 160 }}>
                    <label className="form-label">Monthly price (USD)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: 'var(--text-muted)' }}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={draft}
                        onChange={(e) =>
                          setAddonPriceDrafts((prev) => ({ ...prev, [addon.slug]: e.target.value }))
                        }
                        style={{ maxWidth: 120 }}
                      />
                      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>/mo</span>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    type="button"
                    onClick={() => handleSaveAddonPrice(addon.slug)}
                    disabled={savingAddon || !dirty}
                  >
                    {savingAddon
                      ? <><Icons.Loader size={16} className="spin" /> Saving…</>
                      : <><Icons.Check size={16} /> Save price</>}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Security Settings */}
      <div className="card mb-3">
        <h3 className="card-title mb-3">Security Settings</h3>

        <div className="form-group">
          <label className="form-label">Session Timeout (minutes)</label>
          <input
            type="number"
            className="form-control"
            value={settings.session_timeout_minutes}
            onChange={(e) => setSettings({ ...settings, session_timeout_minutes: parseInt(e.target.value) })}
            style={{ maxWidth: '200px' }}
          />
          <p className="form-hint">How long before inactive users are logged out</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.require_email_verification}
              onChange={(e) => setSettings({ ...settings, require_email_verification: e.target.checked })}
            />
            <div>
              <div style={{ fontWeight: 500 }}>Require Email Verification</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>New users must verify their email before accessing the system</div>
            </div>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.allow_self_registration}
              onChange={(e) => setSettings({ ...settings, allow_self_registration: e.target.checked })}
            />
            <div>
              <div style={{ fontWeight: 500 }}>Allow Self Registration</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Allow new tenants to register without admin approval</div>
            </div>
          </label>
        </div>
      </div>

      {/* Maintenance Mode */}
      <div className="card mb-3">
        <h3 className="card-title mb-3">Maintenance Mode</h3>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.maintenance_mode}
            onChange={(e) => setSettings({ ...settings, maintenance_mode: e.target.checked })}
            style={{ marginTop: '4px' }}
          />
          <div>
            <div style={{ fontWeight: 500 }}>Enable Maintenance Mode</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              When enabled, only super admins can access the system. Regular users will see a maintenance message.
            </div>
          </div>
        </label>

        {settings.maintenance_mode && (
          <div className="alert alert-warning mt-2">
            <Icons.AlertCircle size={18} />
            <span>Maintenance mode is active. Normal users cannot access the system.</span>
          </div>
        )}
      </div>

      {/* System Information */}
      <div className="card mb-3">
        <h3 className="card-title mb-3">System Information</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Version</div>
            <div>1.0.0</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Environment</div>
            <div>Development</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Database</div>
            <div>PostgreSQL</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>API Server</div>
            <div>http://localhost:3001</div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
        <button className="btn btn-secondary" onClick={loadSettings} disabled={saving}>
          <Icons.RefreshCw size={18} />
          Reset
        </button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Icons.Loader size={18} className="spin" />
              Saving...
            </>
          ) : (
            <>
              <Icons.Check size={18} />
              Save Settings
            </>
          )}
        </button>
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SystemSettingsView;
